import { useState, useEffect, useCallback, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { FileText, Upload, Trash2, Download } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DocumentsSection() {
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/files/mine");
      setFiles(res.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setSuccess("");
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post("/files/upload?category=cv", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccess("Fichier téléversé avec succès.");
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = async (fileId) => {
    setError("");
    try {
      await api.delete(`/files/${fileId}`);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div data-testid="documents-section">
      <h2 className="font-semibold text-brand-navy mb-4 flex items-center gap-2">
        <FileText className="w-4 h-4 text-brand-blue" />
        Mes documents ({files.length})
      </h2>

      {error && (
        <div data-testid="documents-error" className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
      )}
      {success && (
        <div data-testid="documents-success" className="mb-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">{success}</div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-3">
        <input
          ref={inputRef}
          data-testid="document-upload-input"
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={handleUpload}
          className="hidden"
          id="cv-file-upload"
        />
        <label
          htmlFor="cv-file-upload"
          data-testid="document-upload-button"
          className={`flex items-center justify-center gap-2 w-full border-2 border-dashed border-slate-300 rounded-lg px-4 py-6 text-sm text-slate-500 cursor-pointer hover:border-brand-turquoise hover:text-brand-turquoise transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}
        >
          <Upload className="w-4 h-4" />
          {uploading ? "Téléversement en cours…" : "Téléverser un CV (PDF, DOC, DOCX — max 5 Mo)"}
        </label>
      </div>

      <div className="space-y-2">
        {files.map((f) => (
          <div key={f.file_id} data-testid={`document-row-${f.file_id}`} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-brand-navy truncate">{f.original_filename}</p>
              <p className="text-xs text-slate-400">
                {(f.size / 1024).toFixed(0)} Ko · {f.created_at ? new Date(f.created_at).toLocaleDateString("fr-CA") : ""}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a
                data-testid={`document-download-${f.file_id}`}
                href={`${API_BASE}/files/${f.file_id}/download`}
                className="text-brand-blue hover:text-brand-navy transition-colors"
                title="Télécharger"
              >
                <Download className="w-4 h-4" />
              </a>
              <button
                data-testid={`document-delete-${f.file_id}`}
                onClick={() => handleDelete(f.file_id)}
                className="text-slate-400 hover:text-red-600 transition-colors"
                title="Supprimer"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {files.length === 0 && (
          <p data-testid="documents-empty" className="text-sm text-slate-400 bg-white rounded-xl border border-slate-200 p-6 text-center">
            Aucun document téléversé.
          </p>
        )}
      </div>
    </div>
  );
}
